import { WORLD } from '../constants';
import { mulberry32 } from '../utils/rng';

interface Mote {
  /** World-space position. */
  x: number;
  y: number;
  /** Drift velocity (world u/s). */
  vx: number;
  vy: number;
  /** Animation phase offset for the breathing alpha. */
  phase: number;
  /** Base radius in world units. */
  r: number;
  /** Hue (degrees). */
  hue: number;
}

/**
 * Ambient dust field — slow-drifting motes scattered across the world.
 *
 * These have no gameplay effect; they're pure atmosphere. Think "particles
 * suspended in a neon nebula". Density is intentionally sparse — heavy dust
 * fights the actual gameplay layers for attention.
 *
 * Motes are confined to the world bounds; if a mote drifts past the edge it
 * wraps around to the opposite side. This way the field is always uniform
 * — no thinning effect over time.
 */
export class DustField {
  public readonly motes: Mote[] = [];

  constructor(count = 240, seed = 0xC0FFEE) {
    const rng = mulberry32(seed);
    // Cool-spectrum hues only — atmosphere, not gameplay food. We pick a
    // wide range centered on blue so motes never look like edible pellets.
    const palette = [195, 220, 240, 265, 280, 300];
    for (let i = 0; i < count; i++) {
      this.motes.push({
        x: rng() * WORLD.SIZE,
        y: rng() * WORLD.SIZE,
        vx: (rng() - 0.5) * 8,
        vy: (rng() - 0.5) * 8,
        phase: rng() * Math.PI * 2,
        r: 0.7 + rng() * 1.4,
        hue: palette[Math.floor(rng() * palette.length)]!,
      });
    }
  }

  step(dt: number): void {
    const motes = this.motes;
    const max = WORLD.SIZE;
    for (let i = 0; i < motes.length; i++) {
      const m = motes[i]!;
      m.x += m.vx * dt;
      m.y += m.vy * dt;
      // Toroidal wrap so the field stays uniform indefinitely.
      if (m.x < 0) m.x += max;
      else if (m.x > max) m.x -= max;
      if (m.y < 0) m.y += max;
      else if (m.y > max) m.y -= max;
    }
  }
}

export type { Mote };
